import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import MarkerClusterer from '@google/markerclustererplus';
import axios from 'axios';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useGoogleMaps } from 'appointment-tool/hooks';
import { EView } from 'appointment-tool/constants/views';
import { useSnackbarContext } from 'common/context';

import { ILocation, IMarker, IAvailableStore } from '../../types';
import { MapWrapper } from '../MapWrapper';
import { MapElement } from '../MapElement';
import { MapLocationBarWrapper } from '../MapLocationBarWrapper';
import { LoadingOverlay } from '../LoadingOverlay';
import { ListButton } from '../ListButton';
import { InfoWindow } from '../InfoWindow';
import { LocationBar } from '../LocationBar';
import { useStateContext } from '../../context';
import { CLUSTERER_OPTIONS, MAP_STYLES } from '../../constants';
import pnlMarker from '../../images/markers/marker.svg';

interface IProps {
    className?: string;
}

const BaseGoogleMaps = ({ className }: IProps) => {
    const mapRef = useRef<HTMLDivElement>(document.createElement('div'));
    const markerClickStore = useRef<IAvailableStore | undefined>();
    const { googleMapsState, setCurrentLocationMarker, setMapObject } =
        useGoogleMaps();
    const { setSnackbarState } = useSnackbarContext();
    const context = useStateContext();
    const {
        availableStores,
        searchLocation,
        searchTerm,
        showMobileMap,
        useCurrentLocation,
        view,
        setCenterLocation,
        setFindingCurrentLocation,
        setSelectedStore,
        setSearchInputValue,
        setSearchLocation,
        setShowMobileMap,
        setUseCurrentLocation,
    } = context;

    const { t } = useTranslation();

    const closeInfoWindow = () => {
        googleMapsState.current.infoWindow.close();
    };

    const handleMapDrag = debounce(() => {
        setUseCurrentLocation(false);
        setSelectedStore(undefined);

        closeInfoWindow();
    }, 100);

    const hasStoresInBounds = () => {
        if (!googleMapsState.current.markers) return;

        return (
            googleMapsState.current.markers.filter(({ marker }) => {
                const markerPosition = marker.getPosition();

                if (!googleMapsState.current.mapObject || !markerPosition) {
                    return true;
                }

                const bounds = googleMapsState.current.mapObject.getBounds();

                if (!bounds) return true;

                return bounds.contains(markerPosition);
            }).length > 0
        );
    };

    const handleBoundsChange = debounce(() => {
        if (
            googleMapsState.current.mapObject &&
            googleMapsState.current.markers &&
            !hasStoresInBounds()
        ) {
            googleMapsState.current.mapObject.setZoom(
                googleMapsState.current.mapObject.getZoom() - 1
            );
        }
    }, 100);

    const handleMapClick = () => {
        googleMapsState.current.infoWindow.close();
    };

    const setInitialMapBounds = () => {
        if (!googleMapsState.current.mapObject) return;

        googleMapsState.current.mapObject.fitBounds(
            googleMapsState.current.defaultBounds,
            0
        );
    };

    const handleCenterChange = debounce(() => {
        if (!googleMapsState.current.mapObject) return;

        const { lat, lng } = googleMapsState.current.mapObject.getCenter();
        const centerLocation = {
            lat: lat(),
            lng: lng(),
        };

        setCenterLocation(centerLocation);
        setSelectedStore(markerClickStore.current);

        markerClickStore.current = undefined;
    }, 100);

    const handleMarkerClusterClick = (cluster: Cluster) => {
        googleMapsState.current.geocoder.geocode(
            { location: cluster.getCenter() },
            () => {}
        );
    };

    const addMarkers = () => {
        if (!availableStores || !googleMapsState.current.mapObject) return;

        googleMapsState.current.markers = availableStores.map((store) => {
            const { lat, lng } = store;
            const storePosition = {
                lat,
                lng,
            };

            const marker = new window.google.maps.Marker({
                position: storePosition,
                icon: {
                    url: pnlMarker,
                    scaledSize: new window.google.maps.Size(40, 52),
                },
                map: googleMapsState.current.mapObject,
                animation: window.google.maps.Animation.DROP,
            });

            marker.addListener('click', () => {
                markerClickStore.current = store;

                setSelectedStore(store);
                setUseCurrentLocation(false);
                setCenterLocation(storePosition);

                panToPosition(storePosition);

                if (googleMapsState.current.infoWindow !== undefined) {
                    googleMapsState.current.infoWindow.open(
                        googleMapsState.current.mapObject,
                        marker
                    );
                }
            });

            return {
                store,
                marker,
            };
        });

        googleMapsState.current.markerClusterer = new MarkerClusterer(
            googleMapsState.current.mapObject,
            googleMapsState.current.markers.map(
                (marker: IMarker) => marker.marker
            ),
            CLUSTERER_OPTIONS
        );

        if (googleMapsState.current.markerClusterer) {
            window.google.maps.event.addListener(
                googleMapsState.current.markerClusterer,
                'clusterclick',
                handleMarkerClusterClick
            );
        }
    };

    const handleUseCurrentLocation = () => {
        setFindingCurrentLocation(true);

        const geoLocationOptions = {
            timeout: 5000,
        };

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const location = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };

                setSearchLocation(location);
                setFindingCurrentLocation(false);
                setSearchInputValue('');
                setUseCurrentLocation(true);

                setCurrentLocationMarker(location);
            },
            () => {
                setFindingCurrentLocation(false);
                setUseCurrentLocation(false);

                setSnackbarState({
                    message: t('appointment.error.currentLocationNotFound'),
                    variant: 'error',
                });
            },
            geoLocationOptions
        );
    };

    const findLocationByZipCode = (zipCodeInput: string) => {
        if (!process.env.REACT_APP_LOCATION_API_URL) return;

        axios
            .post(process.env.REACT_APP_LOCATION_API_URL, {
                zipCode: zipCodeInput,
            })
            .then(({ data: searchLocation }) => {
                setSearchLocation(searchLocation);
            })
            .catch(() => {
                setSnackbarState({
                    message: t('appointment.error.zipCodeNotFound'),
                    variant: 'error',
                });
            });
    };

    const handleSearchTermChange = (searchTerm: string) => {
        if (!process.env.REACT_APP_LOCATION_API_URL) return;

        if (searchTerm === '') {
            setInitialMapBounds();

            setSearchLocation(undefined);

            return;
        }

        setUseCurrentLocation(false);
        setSelectedStore(undefined);

        const zipCodeRegex = /^[0-9]{4}$/;

        if (zipCodeRegex.test(searchTerm)) {
            findLocationByZipCode(searchTerm);

            return;
        }

        googleMapsState.current.geocoder.geocode(
            {
                address: searchTerm,
                region: 'BE',
            },
            (results, status) => {
                if (status === 'OK') {
                    const {
                        location: { lat, lng },
                    } = results[0].geometry;
                    const searchLocation = {
                        lat: lat(),
                        lng: lng(),
                    };

                    setSearchLocation(searchLocation);

                    return;
                }

                setSnackbarState({
                    message: t('appointment.error.locationNotFound'),
                    variant: 'error',
                });
            }
        );
    };

    const panToPosition = (location: ILocation) => {
        if (!googleMapsState.current.mapObject) return;

        googleMapsState.current.mapObject.panTo(location);
        googleMapsState.current.mapObject.setZoom(13);
    };

    const handleListButtonClick = () => {
        setShowMobileMap(false);
    };

    useEffect(() => {
        if (googleMapsState.current.mapObject) return;

        setMapObject(
            new window.google.maps.Map(mapRef.current, {
                disableDefaultUI: true,
                styles: MAP_STYLES,
                zoomControl: !context.isMobile,
            })
        );

        if (!googleMapsState.current.mapObject) return;

        const mapObject = googleMapsState.current.mapObject as google.maps.Map;

        mapObject.addListener('drag', handleMapDrag);
        mapObject.addListener('bounds_changed', handleBoundsChange);
        mapObject.addListener('click', handleMapClick);

        setInitialMapBounds();

        window.google.maps.event.addListener(
            googleMapsState.current.mapObject,
            'idle',
            () => {
                if (!googleMapsState.current.mapObject) return;

                googleMapsState.current.mapObject.addListener(
                    'center_changed',
                    handleCenterChange
                );
            }
        );

        google.maps.event.addListenerOnce(
            googleMapsState.current.mapObject,
            'idle',
            function () {
                if (navigator.geolocation) {
                    handleUseCurrentLocation();
                }
            }
        );

        if (context.availableStores) addMarkers();
    }, []);

    useEffect(() => {
        if (useCurrentLocation) {
            handleUseCurrentLocation();
        }
    }, [useCurrentLocation]);

    useEffect(() => {
        if (searchTerm !== undefined) {
            handleSearchTermChange(searchTerm);
        }
    }, [searchTerm]);

    useEffect(() => {
        if (searchLocation !== undefined) panToPosition(searchLocation);
    }, [searchLocation]);

    useEffect(() => {
        if (view === EView.Appointment) {
            closeInfoWindow();
        }
    }, [view]);

    return (
        <>
            {!availableStores && <LoadingOverlay />}
            <MapWrapper className={className} showMobileMap={showMobileMap}>
                <MapLocationBarWrapper>
                    <LocationBar />
                </MapLocationBarWrapper>
                <MapElement ref={mapRef} />
                <ListButton onClick={handleListButtonClick} />
                {context.selectedStore &&
                    ReactDOM.createPortal(
                        <InfoWindow />,
                        googleMapsState.current.infoWindowNode
                    )}
            </MapWrapper>
        </>
    );
};

export const GoogleMaps = styled(BaseGoogleMaps)``;
